import { defineComponent } from "vue";
import { required, requiredIf } from "@vuelidate/validators";
import useVuelidate from "@vuelidate/core";
import { mapState } from "pinia";

import { IGetUsersByNameQuery, IUsersList } from "../../vue-interfaces/messaging-service/i-user";
import { MessageService } from "../../../services/message-service";
import { MeetingCalendarService } from "../../../services/meeting-calendar-service";
import { formatDate } from "../../../common/vue-helpers/modifiers/value-modifier";
import { formatTime, isToday, roundTimeToNextInterval } from "../../../common/vue-helpers/modifiers/format-time";
import moment from "moment";
import { Container } from "aurelia-dependency-injection";
import { WeeklyMeetingDays } from "../../../enums/WeeklyMeetingDaysEnum";
import { RecurrenceType, RecurrenceTypeEnum } from "../../../enums/RecurrenceTypeEnum";
import { enumToMap } from "../../../common/vue-helpers/modifiers/enum-modifiers";
import { occurenceText } from "../../../common/vue-helpers/modifiers/format-occurence-text";
import { useApp } from "../../../pages-vue/App/useApp";
import type {
    IEditMeetingRequest,
    IMeetingConflictRequest,
    IMeetingParticipants,
    IScheduleMeetingRequest
} from "../../vue-interfaces/messaging-service/i-meeting-calendar";

import DatePicker from "../../vue-custom-elements/DatePicker/DatePicker.vue";
import MeetingTimePicker from "../../vue-custom-elements/MeetingTimePicker/MeetingTimePicker.vue";
import DayPicker from "../../vue-custom-elements/DayPicker/DayPicker.vue";
import TagsInput from "../../vue-custom-elements/TagsInput/TagsInput.vue";

export default defineComponent({
    inject: ["dialogRef"],

    components: {
        TagsInput,
        DatePicker,
        DayPicker,
        MeetingTimePicker
    },

    data() {
        return {
            _messageService: null as MessageService,
            _meetingCalendarService: null as MeetingCalendarService,
            dialogReference: this.dialogRef as any,
            mode: "create" as "create" | "edit",
            initialEventType: "single" as "single" | "recurring",
            selectedEventData: null as any,
            eventDetails: {} as any,
            eventTitle: "" as string,
            attendees: [] as any[],
            eventDate: moment().format("MM/DD/YYYY") as string,
            recurrenceStartDate: moment().format("MM/DD/YYYY") as string,
            recurrenceEndDate: moment().add(1, "months").format("MM/DD/YYYY") as string, // default 1 month ahead
            startTime: "" as string,
            endTime: "" as string,
            description: "" as string,
            recurrenceType: RecurrenceTypeEnum.None as any,
            recurrenceTypeList: [],
            frequency: 1 as number,
            frequencyCount: 1 as number,
            daysOfWeek: "" as string,
            isAllDayEvent: false as boolean,
            isRecurringEvent: false as boolean,
            appStore: null as any,
            isEditSeries: false as boolean,
            initDayPickerValue: [] as number[],
            showConflictWarning: false as boolean,
            debounceTimer: null as any,
            v$: null as any,
            RecurrenceTypeEnum,
            formatDate,
            occurenceText
        };
    },

    validations() {
        return {
            eventTitle: { required },
            attendees: { required: required },
            eventDate: { required },
            startTime: { required: requiredIf(!this.isAllDayEvent) },
            endTime: { required: requiredIf(!this.isAllDayEvent) }
        };
    },

    setup() {
        return { v$: useVuelidate() };
    },

    computed: {
        ...mapState(useApp, ["loggedInUserDetails"]),

        dialogTitle(): string {
            return this.mode === "edit" ? "Event" : "New Event";
        },

        submitLabel(): string {
            return this.mode === "edit" ? "Send" : "Save";
        },

        isFormValid(): boolean {
            return !this.v$.$invalid && !!this.eventTitle && this.attendees.length > 0 && !!this.eventDate;
        },

        startMinTime() {
            if (this.mode === "edit") {
                return "12:00 AM";
            }
            if (!isToday(this.eventDate)) {
                return "12:00 AM";
            }

            return formatTime(roundTimeToNextInterval(moment()));
        },

        endMinTime() {
            if (!this.startTime) {
                return "12:15 AM";
            }
            return formatTime(moment(this.startTime, "hh:mm A").add(15, "minutes"));
        },

        showFrequency() {
            return (
                this.recurrenceType === RecurrenceTypeEnum.Weekly || this.recurrenceType === RecurrenceTypeEnum.Monthly
            );
        },

        isEditingSingleOccurence() {
            if (this.initialEventType === "single") {
                return false;
            } else {
                if (this.isRecurringEvent) {
                    return !this.isEditSeries && this.mode === "edit";
                }
            }
            return false;
        },

        // Combine multiple variables to add combined watcher
        updateConflictStatus() {
            return {
                recurrenceEndDate: this.recurrenceEndDate,
                weeklyDaysOfWeek: this.daysOfWeek,
                frequency: this.frequency,
                recurrenceType: this.recurrenceType
            };
        }
    },

    created() {
        this._messageService = Container.instance.get(MessageService);
        this._meetingCalendarService = Container.instance.get(MeetingCalendarService);
    },

    mounted() {
        this.mode = this.dialogReference?.data?.mode || "create";
        this.isEditSeries = this.dialogReference?.data?.isEditSeries || false;
        if (this.mode === "edit") {
            this.bindEditData();
        } else {
            this.setTodayStartAndEndTime();
        }

        if (this.mode === "edit" && this.initialEventType === "recurring") {
            // Show only recurring options if user is editing recurring meetings
            this.recurrenceTypeList = Array.from(enumToMap(RecurrenceType).values()).slice(1, 4);
        } else {
            // Show only first 4 values for calendar feature
            this.recurrenceTypeList = Array.from(enumToMap(RecurrenceType).values()).slice(0, 4);
        }
    },

    watch: {
        updateConflictStatus: {
            handler() {
                if (this.recurrenceType === RecurrenceTypeEnum.Weekly) {
                    if (this.daysOfWeek.trim().length) {
                        this.checkUserConflicts(this.attendees, 1000);
                    }
                } else {
                    this.checkUserConflicts(this.attendees, 1000);
                }
            },
            deep: true
        }
    },

    methods: {
        bindEditData() {
            const selectedEvent = this.dialogReference?.data?.selectedEvent;
            if (!selectedEvent) {
                return;
            }

            this.eventTitle = selectedEvent.title || "";
            this.description = selectedEvent.extendedProps?.description || "";
            this.frequency = selectedEvent.extendedProps?.frequency || 1;
            this.eventDetails = {
                meetingId: selectedEvent.id,
                ...selectedEvent.extendedProps
            };
            this.getEditEventAttendees(selectedEvent.extendedProps?.participants);

            const startMoment = moment(selectedEvent.startStr || selectedEvent.start);
            const endMoment = moment(selectedEvent.endStr || selectedEvent.end || selectedEvent.start);
            if (startMoment.isValid()) {
                this.eventDate = startMoment.format("MM/DD/YY");
                this.startTime = startMoment.format("hh:mm A");
            }

            if (endMoment.isValid()) {
                this.endTime = endMoment.format("hh:mm A");
            }

            const recurringType = selectedEvent.extendedProps?.recurringType;

            if (recurringType !== undefined && recurringType !== null) {
                this.recurrenceType = String(recurringType) as any;

                if (this.recurrenceType === RecurrenceTypeEnum.Weekly) {
                    this.frequencyCount = 5;
                } else if (this.recurrenceType === RecurrenceTypeEnum.Monthly) {
                    this.frequencyCount = 12;
                } else {
                    this.frequencyCount = 1;
                }

                this.isRecurringEvent = Number(recurringType) > 0;
                this.initialEventType = Number(recurringType) > 0 ? "recurring" : "single";
                this.recurrenceStartDate = moment(selectedEvent.extendedProps?.recurrenceStartDate).format("MM/DD/YY");
                this.recurrenceEndDate = moment(selectedEvent.extendedProps?.recurrenceEndDate).format("MM/DD/YY");
            }

            this.isAllDayEvent = !!selectedEvent.allDay;
            const weeklyDaysOfWeekStr = selectedEvent.extendedProps?.weeklyDaysOfWeekStr;
            if (weeklyDaysOfWeekStr.length) {
                const arr = weeklyDaysOfWeekStr.split(", ");
                this.initDayPickerValue = arr.map((day: string) => {
                    return WeeklyMeetingDays.getValueUsingKey(day);
                });
                this.daysOfWeek = this.initDayPickerValue.join(",");
            }
        },

        changeDate(date: any, name: string) {
            if (!date) {
                return;
            }

            const value = moment(date).format("MM/DD/YY");
            if (isToday(date)) {
                this.setTodayStartAndEndTime();
            } else {
                this.startTime = "12:00 AM";
                this.endTime = "12:15 AM";
            }

            if (name === "recurrenceEndDate") {
                this.recurrenceEndDate = value;
                return;
            }

            this.eventDate = value;
            this.recurrenceStartDate = value;
            this.recurrenceEndDate = moment(this.eventDate).add(1, "months").format("MM/DD/YY");
            // update conflict status
            this.checkUserConflicts(this.attendees);
        },

        getAttendeeIds(): string[] {
            if (!Array.isArray(this.attendees)) {
                return [];
            }

            return this.attendees
                .map((attendee: any) => {
                    if (typeof attendee === "string") {
                        return attendee;
                    }

                    return attendee?.value || attendee?.id || "";
                })
                .filter((id: string) => !!id);
        },

        getWeeklyDaysOfWeek(): number[] {
            if (!this.daysOfWeek) {
                return [];
            }

            return this.daysOfWeek
                .split(",")
                .map((day) => parseInt(day, 10))
                .filter((day) => !isNaN(day));
        },

        getDurationInMinutes(): number {
            if (this.isAllDayEvent) {
                return 1439;
            }

            const startMoment = moment(`${this.eventDate} ${this.startTime}`, [
                "MM/DD/YY hh:mm A",
                "MM/DD/YYYY hh:mm A"
            ]);
            const endMoment = moment(`${this.eventDate} ${this.endTime}`, ["MM/DD/YY hh:mm A", "MM/DD/YYYY hh:mm A"]);

            if (!startMoment.isValid() || !endMoment.isValid()) {
                return 15;
            }

            const duration = endMoment.diff(startMoment, "minutes");
            return duration > 0 ? duration : 15;
        },

        getMeetingStartTime(): string {
            if (this.isAllDayEvent) {
                return moment.utc(this.eventDate).startOf("day").toISOString();
            }

            const dateTime = moment(`${this.eventDate} ${this.startTime}`, ["MM/DD/YY hh:mm A", "MM/DD/YYYY hh:mm A"]);
            if (dateTime.isValid()) {
                return dateTime.toISOString();
            }

            return moment().toISOString();
        },

        buildEditPayload(): IEditMeetingRequest {
            const startTime = this.getMeetingStartTime();
            let payload: IEditMeetingRequest = {
                title: this.eventTitle || "Meeting",
                userIds: this.getAttendeeIds().length
                    ? this.getAttendeeIds()
                    : [this.loggedInUserDetails.loginId || ""],
                meetingStartTime: startTime,
                duration: this.getDurationInMinutes(),
                description: this.description || "",
                editRecurrence: this.isRecurringEvent,
                conversationId: this.eventDetails.conversationId || "",
                isAllDayEvent: this.isAllDayEvent,
                meetingId: this.eventDetails.meetingId
            };

            if (this.isRecurringEvent) {
                // send editRecurrence true while converting single event to recurring event
                payload.editRecurrence =
                    this.initialEventType === "single" ? true : this.isRecurringEvent && this.isEditSeries;
                payload.isRecurring = true;
                const recurringPayload = this.buildRecurringPayload();
                payload = { ...payload, ...recurringPayload };
            }
            return payload;
        },

        buildSchedulePayload(): IScheduleMeetingRequest {
            const startTime = this.getMeetingStartTime();
            let payload: IScheduleMeetingRequest = {
                title: this.eventTitle || "New Meeting",
                userIds: this.getAttendeeIds(),
                meetingStartTime: startTime,
                duration: this.getDurationInMinutes(),
                description: this.description || "",
                isRecurring: this.isRecurringEvent,
                isAllDayEvent: this.isAllDayEvent
            };
            if (this.isRecurringEvent) {
                const recurringPayload = this.buildRecurringPayload();
                payload = { ...payload, ...recurringPayload };
            }
            return payload;
        },

        buildRecurringPayload() {
            const payload: any = {};
            const startTime = this.getMeetingStartTime();
            const recurrenceEndDate = moment(this.recurrenceEndDate, ["MM/DD/YY", "MM/DD/YYYY"]);
            const recurrenceEndDateIso = recurrenceEndDate.isValid()
                ? recurrenceEndDate.endOf("day").toISOString()
                : moment(this.eventDate, ["MM/DD/YY", "MM/DD/YYYY"]).endOf("day").toISOString();
            payload.recurring = parseInt(this.recurrenceType, 10);
            payload.recurrenceEndDate = recurrenceEndDateIso;
            if (this.recurrenceType === RecurrenceTypeEnum.Weekly) {
                payload.weeklyDaysOfWeek = this.getWeeklyDaysOfWeek();
                payload.frequency = this.frequency;
            }
            if (this.recurrenceType === RecurrenceTypeEnum.Monthly) {
                payload.onMonthlyDate = startTime;
                payload.frequency = this.frequency;
            }
            return payload;
        },

        async getUsersList(filter: string, limit: number) {
            const query: IGetUsersByNameQuery = {
                page: 1,
                pageLength: 25,
                term: filter.trim(),
                conversationParticipantUsers: this.attendees.map((user) => user.value)
            };
            const data = await this._messageService.getUserByName(query);
            return this.getDisplayUsers(data.items);
        },

        getDisplayUsers(data: IUsersList[]) {
            if (data) {
                const filteredItems = data.filter((user) => !this.attendees.includes(user.id));
                return filteredItems.map((user) => ({
                    name: `${user.lastName ? user.lastName + ", " : ""}${user.firstName}`,
                    value: user.id,
                    firstName: user.firstName,
                    lastName: user.lastName,
                    emailId: user.emailId
                }));
            }
            return [];
        },

        getEditEventAttendees(users: IMeetingParticipants[]) {
            this.attendees = users.map((user) => ({
                name: `${user.lastName ? user.lastName + ", " : ""}${user.firstName}`,
                value: user.userId,
                firstName: user.firstName,
                lastName: user.lastName,
                emailId: user.email
            }));
        },

        async updateTags(value: any) {
            if (this.mode !== "create") {
                this.attendees = value;
            } else if (value.length > this.attendees.length) {
                // check for conflicts if users are added
                this.attendees = value;
                await this.checkUserConflicts(value);
            } else {
                // update warning if users are removed
                this.showConflictWarning = false;
                for (const user of value) {
                    if (!user.isAvailable) {
                        this.showConflictWarning = true;
                        break;
                    }
                }
                this.attendees = value;
            }
            this.v$.attendees.$touch();
        },

        async checkUserConflicts(usersList: any[], delay: number = 650) {
            if (usersList.length && this.mode !== "edit") {
                if (this.debounceTimer) {
                    clearTimeout(this.debounceTimer);
                }

                this.debounceTimer = setTimeout(async () => {
                    const userIds = usersList.map((user) => user.value);
                    let payload: IMeetingConflictRequest = {
                        userIds,
                        duration: this.getDurationInMinutes(),
                        startDateTime: this.getMeetingStartTime(),
                        isRecurring: this.isRecurringEvent
                    };
                    if (this.isRecurringEvent) {
                        const recurringPayload = this.buildRecurringPayload();
                        payload = { ...payload, ...recurringPayload };
                    }
                    const response = await this._meetingCalendarService.checkConflict(payload);
                    const statusMap = Object.fromEntries(response.users.map(({ userId, ...rest }) => [userId, rest]));
                    this.showConflictWarning = false;
                    const result = usersList.map((user) => {
                        if (!statusMap[user.value].isAvailable) {
                            this.showConflictWarning = true;
                        }
                        return { ...user, ...statusMap[user.value] };
                    });

                    this.attendees = result;
                }, delay);
            }
        },

        setTodayStartAndEndTime() {
            const start = roundTimeToNextInterval(moment());
            const end = start.clone().add(15, "minutes");

            this.startTime = formatTime(start);
            this.endTime = formatTime(end);
        },

        toggleAllDayEvent() {
            this.isAllDayEvent = !this.isAllDayEvent;
        },

        makeRecurringEvent(value: RecurrenceTypeEnum) {
            if (value === RecurrenceTypeEnum.None) {
                this.isRecurringEvent = false;
            } else {
                this.isRecurringEvent = true;
            }
        },

        handleRecurrenceChange(event: any) {
            const value = event.target.value;
            this.recurrenceType = value;

            this.makeRecurringEvent(value);

            if (value === RecurrenceTypeEnum.Weekly) {
                this.frequencyCount = 5;
            } else if (value === RecurrenceTypeEnum.Monthly) {
                this.frequencyCount = 12;
            } else {
                this.frequencyCount = 1;
            }
        },
        handleFrequencyChange(event: any) {
            this.frequency = event.target.value;
        },

        updateDaysOfWeek(value: string) {
            this.daysOfWeek = value;
        },

        cancel() {
            this.dialogReference.close();
        },

        async save() {
            try {
                // Touch all fields to show validation errors
                await this.v$.$validate();

                if (this.v$.$invalid) {
                    return;
                }

                if (this.mode === "edit") {
                    const editPayload = this.buildEditPayload();
                    await this._meetingCalendarService.editMeeting(editPayload);
                } else {
                    const payload = this.buildSchedulePayload();
                    await this._meetingCalendarService.scheduleMeeting(payload);
                }
                this.dialogReference.close("success");
            } catch (error) {
                console.error("Failed to submit meeting event", error);
            }
        }
    },

    beforeUnmount() {
        if (this.debounceTimer) {
            clearTimeout(this.debounceTimer);
        }
    }
});
